import { useState, useEffect } from "react";
import {
  Users, Plus, Pencil, Trash2, Check, X, Loader2,
  DollarSign, Link2, Copy, Settings, Upload, Image,
  Video, FileText, ArrowUpRight, CheckCircle, XCircle,
  Eye, BarChart3
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";

interface Affiliate {
  id: string; user_id: string; name: string; email: string; code: string;
  commission_type: string; commission_percentage: number; commission_fixed: number;
  is_active: boolean; total_earned: number; total_paid: number;
  status: string; pix_key: string | null; pix_type: string | null;
  bio: string | null; created_at: string;
}

interface Props {
  restaurants: any[];
  logAction: (action: string, entityType: string, entityId: string, details?: any) => void;
}

const emptyForm = {
  name: "", email: "", code: "", commission_type: "recurring",
  commission_percentage: 10, commission_fixed: 0, is_active: true,
};

const AdminAffiliatesTab = ({ restaurants, logAction }: Props) => {
  const { toast } = useToast();
  const [affiliates, setAffiliates] = useState<Affiliate[]>([]);
  const [referrals, setReferrals] = useState<any[]>([]);
  const [clicks, setClicks] = useState<any[]>([]);
  const [withdrawals, setWithdrawals] = useState<any[]>([]);
  const [materials, setMaterials] = useState<any[]>([]);
  const [commissionConfigs, setCommissionConfigs] = useState<any[]>([]);
  const [plans, setPlans] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Affiliate | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [selectedAffiliate, setSelectedAffiliate] = useState<string | null>(null);
  const [materialDialogOpen, setMaterialDialogOpen] = useState(false);
  const [materialForm, setMaterialForm] = useState({ title: "", description: "", type: "image", content: "", platform: "all" });
  const [materialFile, setMaterialFile] = useState<File | null>(null);
  const [savingMaterial, setSavingMaterial] = useState(false);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    const [affRes, refRes, clicksRes, wdRes, matsRes, configRes, plansRes] = await Promise.all([
      supabase.from("affiliates").select("*").order("created_at", { ascending: false }),
      supabase.from("affiliate_referrals").select("*").order("created_at", { ascending: false }),
      supabase.from("affiliate_clicks").select("*").order("created_at", { ascending: false }),
      supabase.from("affiliate_withdrawals").select("*").order("created_at", { ascending: false }),
      supabase.from("affiliate_materials").select("*").order("sort_order"),
      supabase.from("affiliate_commission_config").select("*"),
      supabase.from("plans").select("*").eq("is_active", true).order("sort_order"),
    ]);
    setAffiliates((affRes.data as any[]) || []);
    setReferrals((refRes.data as any[]) || []);
    setClicks((clicksRes.data as any[]) || []);
    setWithdrawals((wdRes.data as any[]) || []);
    setMaterials((matsRes.data as any[]) || []);
    setCommissionConfigs((configRes.data as any[]) || []);
    setPlans((plansRes.data as any[]) || []);
    setLoading(false);
  };

  const generateCode = (name: string) => {
    const base = name.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 6);
    return base + Math.random().toString(36).substring(2, 6).toUpperCase();
  };

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyForm, code: generateCode("AFF") });
    setDialogOpen(true);
  };

  const openEdit = (a: Affiliate) => {
    setEditing(a);
    setForm({
      name: a.name, email: a.email, code: a.code,
      commission_type: a.commission_type, commission_percentage: a.commission_percentage,
      commission_fixed: a.commission_fixed, is_active: a.is_active,
    });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.name || !form.email || !form.code) {
      toast({ title: "Preencha todos os campos", variant: "destructive" });
      return;
    }
    setSaving(true);

    if (editing) {
      const { data, error } = await supabase.from("affiliates")
        .update({
          name: form.name, email: form.email, code: form.code,
          commission_type: form.commission_type, commission_percentage: form.commission_percentage,
          commission_fixed: form.commission_fixed, is_active: form.is_active,
        })
        .eq("id", editing.id).select().single();
      if (error) toast({ title: "Erro", description: error.message, variant: "destructive" });
      else {
        setAffiliates(prev => prev.map(a => a.id === editing.id ? data as Affiliate : a));
        logAction("affiliate_updated", "affiliate", editing.id, { name: form.name });
        toast({ title: "Afiliado atualizado!" });
        setDialogOpen(false);
      }
    } else {
      const { data, error } = await supabase.from("affiliates")
        .insert({
          user_id: crypto.randomUUID(),
          name: form.name, email: form.email, code: form.code,
          commission_type: form.commission_type, commission_percentage: form.commission_percentage,
          commission_fixed: form.commission_fixed, is_active: form.is_active, status: "active",
        })
        .select().single();
      if (error) toast({ title: "Erro", description: error.message, variant: "destructive" });
      else {
        setAffiliates(prev => [data as Affiliate, ...prev]);
        logAction("affiliate_created", "affiliate", data.id, { name: form.name });
        toast({ title: "Afiliado criado!" });
        setDialogOpen(false);
      }
    }
    setSaving(false);
  };

  const deleteAffiliate = async (a: Affiliate) => {
    if (!confirm(`Excluir afiliado "${a.name}"?`)) return;
    const { error } = await supabase.from("affiliates").delete().eq("id", a.id);
    if (error) toast({ title: "Erro", description: error.message, variant: "destructive" });
    else {
      setAffiliates(prev => prev.filter(x => x.id !== a.id));
      logAction("affiliate_deleted", "affiliate", a.id, { name: a.name });
      toast({ title: "Afiliado excluído" });
    }
  };

  const approveAffiliate = async (a: Affiliate) => {
    const { error } = await supabase.from("affiliates").update({ status: "active" }).eq("id", a.id);
    if (!error) {
      setAffiliates(prev => prev.map(x => x.id === a.id ? { ...x, status: "active" } : x));
      toast({ title: "Afiliado aprovado!" });
    }
  };

  const rejectAffiliate = async (a: Affiliate) => {
    const { error } = await supabase.from("affiliates").update({ status: "rejected" }).eq("id", a.id);
    if (!error) {
      setAffiliates(prev => prev.map(x => x.id === a.id ? { ...x, status: "rejected" } : x));
      toast({ title: "Afiliado rejeitado" });
    }
  };

  const copyLink = (code: string) => {
    navigator.clipboard.writeText(`${window.location.origin}/cadastro?ref=${code}`);
    toast({ title: "Link copiado!" });
  };

  const fmt = (v: number) => `R$ ${v.toFixed(2).replace(".", ",")}`;

  // Commission config per plan
  const saveCommissionConfig = async (planId: string, percentage: number, type: string) => {
    const existing = commissionConfigs.find((c: any) => c.plan_id === planId);
    if (existing) {
      const { data, error } = await supabase.from("affiliate_commission_config")
        .update({ commission_percentage: percentage, commission_type: type })
        .eq("id", existing.id).select().single();
      if (!error && data) {
        setCommissionConfigs(prev => prev.map(c => c.id === existing.id ? data : c));
        toast({ title: "Comissão atualizada!" });
      }
    } else {
      const { data, error } = await supabase.from("affiliate_commission_config")
        .insert({ plan_id: planId, commission_percentage: percentage, commission_type: type })
        .select().single();
      if (!error && data) {
        setCommissionConfigs(prev => [...prev, data]);
        toast({ title: "Comissão configurada!" });
      }
    }
  };

  // Withdrawal management
  const updateWithdrawal = async (id: string, status: string) => {
    const { data: { user } } = await supabase.auth.getUser();
    const { error } = await supabase.from("affiliate_withdrawals")
      .update({ status, processed_at: new Date().toISOString(), processed_by: user?.id })
      .eq("id", id);
    if (!error) {
      setWithdrawals(prev => prev.map(w => w.id === id ? { ...w, status } : w));
      if (status === "paid") {
        const wd = withdrawals.find(w => w.id === id);
        if (wd) {
          await supabase.from("affiliates")
            .update({ total_paid: (affiliates.find(a => a.id === wd.affiliate_id)?.total_paid || 0) + wd.amount })
            .eq("id", wd.affiliate_id);
        }
      }
      toast({ title: `Saque ${status === "approved" ? "aprovado" : status === "paid" ? "pago" : "rejeitado"}!` });
    }
  };

  // Materials
  const saveMaterial = async () => {
    setSavingMaterial(true);
    let fileUrl = null;

    if (materialFile) {
      const ext = materialFile.name.split(".").pop();
      const path = `materials/${Date.now()}.${ext}`;
      const { error: upErr } = await supabase.storage.from("affiliate-materials").upload(path, materialFile);
      if (upErr) {
        toast({ title: "Erro no upload", description: upErr.message, variant: "destructive" });
        setSavingMaterial(false);
        return;
      }
      const { data: urlData } = supabase.storage.from("affiliate-materials").getPublicUrl(path);
      fileUrl = urlData.publicUrl;
    }

    const { data: { user } } = await supabase.auth.getUser();
    const { data, error } = await supabase.from("affiliate_materials").insert({
      title: materialForm.title,
      description: materialForm.description || null,
      type: materialForm.type,
      content: materialForm.content || null,
      file_url: fileUrl,
      platform: materialForm.platform,
      created_by: user?.id || "",
    }).select().single();

    if (error) {
      toast({ title: "Erro", description: error.message, variant: "destructive" });
    } else {
      setMaterials(prev => [...prev, data]);
      toast({ title: "Material adicionado!" });
      setMaterialDialogOpen(false);
      setMaterialForm({ title: "", description: "", type: "image", content: "", platform: "all" });
      setMaterialFile(null);
    }
    setSavingMaterial(false);
  };

  const deleteMaterial = async (id: string) => {
    if (!confirm("Excluir este material?")) return;
    const { error } = await supabase.from("affiliate_materials").delete().eq("id", id);
    if (!error) {
      setMaterials(prev => prev.filter(m => m.id !== id));
      toast({ title: "Material excluído" });
    }
  };

  // Charts
  const clicksBySource = clicks.reduce((acc: any, c) => {
    const src = c.utm_source || "direto";
    acc[src] = (acc[src] || 0) + 1;
    return acc;
  }, {});
  const sourceChartData = Object.entries(clicksBySource).map(([source, count]) => ({ source, cliques: count }));

  if (loading) return <div className="flex justify-center p-12"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  const pendingAffiliates = affiliates.filter(a => a.status === "pending");
  const pendingWithdrawals = withdrawals.filter(w => w.status === "pending");
  const totalMonthlyRevenue = referrals.filter(r => {
    const d = new Date(r.created_at);
    const now = new Date();
    return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  }).reduce((s, r) => s + r.commission_earned, 0);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display font-semibold text-lg">Programa de Afiliados</h2>
          <p className="text-xs text-muted-foreground">
            {affiliates.length} afiliados · {referrals.length} indicações
            {pendingAffiliates.length > 0 && ` · ${pendingAffiliates.length} pendentes`}
          </p>
        </div>
        <Button onClick={openCreate} size="sm" className="bg-gradient-primary text-primary-foreground">
          <Plus className="h-4 w-4 mr-2" />Novo Afiliado
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {[
          { label: "Afiliados Ativos", value: affiliates.filter(a => a.is_active && a.status === "active").length },
          { label: "Indicações", value: referrals.length },
          { label: "Comissões Geradas", value: fmt(affiliates.reduce((s, a) => s + a.total_earned, 0)) },
          { label: "Comissões Pagas", value: fmt(affiliates.reduce((s, a) => s + a.total_paid, 0)) },
          { label: "Receita Mensal Aff.", value: fmt(totalMonthlyRevenue) },
        ].map(s => (
          <div key={s.label} className="rounded-xl border border-border bg-card p-4">
            <p className="text-[10px] text-muted-foreground">{s.label}</p>
            <p className="text-lg font-bold font-display">{s.value}</p>
          </div>
        ))}
      </div>

      <Tabs defaultValue="affiliates" className="space-y-4">
        <TabsList className="bg-secondary/50 w-full justify-start overflow-x-auto">
          <TabsTrigger value="affiliates" className="text-xs">Afiliados</TabsTrigger>
          <TabsTrigger value="commissions" className="text-xs">Comissões por Plano</TabsTrigger>
          <TabsTrigger value="withdrawals" className="text-xs">
            Saques {pendingWithdrawals.length > 0 && <Badge variant="destructive" className="ml-1 text-[9px] px-1">{pendingWithdrawals.length}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="materials" className="text-xs">Materiais</TabsTrigger>
          <TabsTrigger value="analytics" className="text-xs">Analytics</TabsTrigger>
        </TabsList>

        {/* Affiliates Tab */}
        <TabsContent value="affiliates" className="space-y-4">
          {/* Pending approvals */}
          {pendingAffiliates.length > 0 && (
            <div className="rounded-xl border-2 border-orange-500/30 bg-orange-50 dark:bg-orange-950/20 p-4 space-y-3">
              <h3 className="text-sm font-display font-semibold text-orange-700 dark:text-orange-400">
                Aguardando Aprovação ({pendingAffiliates.length})
              </h3>
              {pendingAffiliates.map(a => (
                <div key={a.id} className="flex items-center justify-between p-3 rounded-lg bg-card">
                  <div>
                    <p className="text-sm font-medium">{a.name}</p>
                    <p className="text-[10px] text-muted-foreground">{a.email} · {a.bio || "Sem descrição"}</p>
                  </div>
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" onClick={() => approveAffiliate(a)} className="text-green-600">
                      <CheckCircle className="h-4 w-4" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => rejectAffiliate(a)} className="text-destructive">
                      <XCircle className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Affiliates list */}
          {affiliates.filter(a => a.status !== "pending").length === 0 ? (
            <div className="rounded-xl border border-border bg-card p-12 text-center text-muted-foreground">
              <Users className="h-8 w-8 mx-auto mb-2 opacity-30" /><p>Nenhum afiliado</p>
            </div>
          ) : (
            <div className="rounded-xl border border-border bg-card overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="text-xs text-muted-foreground border-b border-border">
                    <th className="text-left p-4 font-medium">Afiliado</th>
                    <th className="text-left p-4 font-medium hidden md:table-cell">Código</th>
                    <th className="text-left p-4 font-medium hidden md:table-cell">Comissão</th>
                    <th className="text-left p-4 font-medium">Ganhos</th>
                    <th className="text-left p-4 font-medium hidden lg:table-cell">Cliques</th>
                    <th className="text-left p-4 font-medium hidden lg:table-cell">Indicações</th>
                    <th className="text-left p-4 font-medium">Status</th>
                    <th className="text-left p-4 font-medium">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {affiliates.filter(a => a.status !== "pending").map(a => {
                    const refs = referrals.filter(r => r.affiliate_id === a.id);
                    const affClicks = clicks.filter(c => c.affiliate_id === a.id);
                    return (
                      <tr key={a.id} className={`border-b border-border/50 hover:bg-secondary/30 ${!a.is_active ? "opacity-50" : ""}`}>
                        <td className="p-4">
                          <p className="text-sm font-medium">{a.name}</p>
                          <p className="text-[10px] text-muted-foreground">{a.email}</p>
                        </td>
                        <td className="p-4 hidden md:table-cell">
                          <div className="flex items-center gap-1">
                            <code className="text-xs bg-secondary px-2 py-0.5 rounded">{a.code}</code>
                            <button onClick={() => copyLink(a.code)} className="p-1 hover:bg-secondary rounded"><Copy className="h-3 w-3 text-muted-foreground" /></button>
                          </div>
                        </td>
                        <td className="p-4 text-sm hidden md:table-cell">
                          {a.commission_type === "recurring" ? "Recorrente" : "Única"} · {a.commission_percentage}%
                        </td>
                        <td className="p-4 text-sm font-medium">{fmt(a.total_earned)}</td>
                        <td className="p-4 text-sm hidden lg:table-cell">{affClicks.length}</td>
                        <td className="p-4 text-sm hidden lg:table-cell">{refs.length}</td>
                        <td className="p-4">
                          <Badge variant={a.is_active && a.status === "active" ? "default" : "secondary"} className="text-[10px]">
                            {a.status === "rejected" ? "Rejeitado" : a.is_active ? "Ativo" : "Inativo"}
                          </Badge>
                        </td>
                        <td className="p-4">
                          <div className="flex items-center gap-1">
                            <button onClick={() => setSelectedAffiliate(selectedAffiliate === a.id ? null : a.id)} className="p-1.5 rounded-lg hover:bg-secondary" title="Ver indicações">
                              <Eye className="h-4 w-4 text-muted-foreground" />
                            </button>
                            <button onClick={() => openEdit(a)} className="p-1.5 rounded-lg hover:bg-secondary"><Pencil className="h-4 w-4 text-muted-foreground" /></button>
                            <button onClick={() => deleteAffiliate(a)} className="p-1.5 rounded-lg hover:bg-destructive/10"><Trash2 className="h-4 w-4 text-destructive" /></button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Referrals detail */}
          {selectedAffiliate && (
            <div className="rounded-xl border border-border bg-card p-4 space-y-3">
              <h3 className="font-display font-semibold text-sm">
                Indicações de {affiliates.find(a => a.id === selectedAffiliate)?.name}
              </h3>
              {referrals.filter(r => r.affiliate_id === selectedAffiliate).length === 0 ? (
                <p className="text-xs text-muted-foreground">Nenhuma indicação ainda.</p>
              ) : (
                <div className="space-y-2">
                  {referrals.filter(r => r.affiliate_id === selectedAffiliate).map(ref => {
                    const rest = restaurants.find(r => r.id === ref.restaurant_id);
                    return (
                      <div key={ref.id} className="flex items-center justify-between p-3 rounded-lg bg-secondary/50">
                        <div>
                          <p className="text-sm font-medium">{rest?.name || "Restaurante removido"}</p>
                          <p className="text-[10px] text-muted-foreground">{new Date(ref.created_at).toLocaleDateString("pt-BR")}</p>
                        </div>
                        <div className="text-right">
                          <Badge variant={ref.status === "active" ? "default" : "secondary"} className="text-[10px]">
                            {ref.status === "active" ? "Ativo" : ref.status}
                          </Badge>
                          <p className="text-xs font-medium mt-1">{fmt(ref.commission_earned)}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </TabsContent>

        {/* Commission Config Tab */}
        <TabsContent value="commissions" className="space-y-4">
          <div className="rounded-xl border border-border bg-card p-4">
            <h3 className="text-sm font-display font-semibold mb-4 flex items-center gap-2">
              <Settings className="h-4 w-4 text-primary" /> Configuração de Comissão por Plano
            </h3>
            <p className="text-xs text-muted-foreground mb-4">
              Defina a porcentagem de comissão para cada plano. Planos gratuitos não geram comissão.
            </p>
            <div className="space-y-3">
              {plans.map(plan => {
                const config = commissionConfigs.find((c: any) => c.plan_id === plan.id);
                const isFree = plan.price_monthly === 0;
                return (
                  <div key={plan.id} className={`p-4 rounded-lg border border-border ${isFree ? "opacity-50" : ""}`}>
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <p className="text-sm font-medium">{plan.name}</p>
                        <p className="text-[10px] text-muted-foreground">
                          {isFree ? "Gratuito — sem comissão" : `R$ ${plan.price_monthly}/mês · R$ ${plan.price_yearly || "—"}/ano`}
                        </p>
                      </div>
                    </div>
                    {!isFree && (
                      <div className="grid grid-cols-3 gap-3">
                        <div className="space-y-1">
                          <Label className="text-[10px]">Comissão (%)</Label>
                          <Input
                            type="number" min="0" max="100" step="0.5"
                            defaultValue={config?.commission_percentage || 0}
                            className="h-8 text-sm"
                            onBlur={e => {
                              const val = parseFloat(e.target.value) || 0;
                              const type = config?.commission_type || "one_time";
                              saveCommissionConfig(plan.id, val, type);
                            }}
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-[10px]">Tipo</Label>
                          <Select
                            defaultValue={config?.commission_type || "one_time"}
                            onValueChange={v => {
                              const pct = config?.commission_percentage || 0;
                              saveCommissionConfig(plan.id, pct, v);
                            }}
                          >
                            <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="one_time">Única</SelectItem>
                              <SelectItem value="recurring">Recorrente</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-1">
                          <Label className="text-[10px]">Status</Label>
                          <p className="text-xs pt-1.5">
                            {config ? (
                              <Badge variant="default" className="text-[9px]">Configurado</Badge>
                            ) : (
                              <Badge variant="secondary" className="text-[9px]">Não configurado</Badge>
                            )}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </TabsContent>

        {/* Withdrawals Tab */}
        <TabsContent value="withdrawals" className="space-y-4">
          {withdrawals.length === 0 ? (
            <div className="rounded-xl border border-border bg-card p-12 text-center text-muted-foreground">
              <DollarSign className="h-8 w-8 mx-auto mb-2 opacity-30" />
              <p className="text-sm">Nenhuma solicitação de saque</p>
            </div>
          ) : (
            <div className="rounded-xl border border-border bg-card overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="text-xs text-muted-foreground border-b border-border">
                    <th className="text-left p-4 font-medium">Afiliado</th>
                    <th className="text-left p-4 font-medium">Valor</th>
                    <th className="text-left p-4 font-medium hidden md:table-cell">PIX</th>
                    <th className="text-left p-4 font-medium">Data</th>
                    <th className="text-left p-4 font-medium">Status</th>
                    <th className="text-left p-4 font-medium">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {withdrawals.map(w => {
                    const aff = affiliates.find(a => a.id === w.affiliate_id);
                    return (
                      <tr key={w.id} className="border-b border-border/50">
                        <td className="p-4 text-sm">{aff?.name || "—"}</td>
                        <td className="p-4 text-sm font-medium">{fmt(w.amount)}</td>
                        <td className="p-4 text-xs hidden md:table-cell">{w.pix_key || "—"} ({w.pix_type || "—"})</td>
                        <td className="p-4 text-xs">{new Date(w.created_at).toLocaleDateString("pt-BR")}</td>
                        <td className="p-4">
                          <Badge variant={w.status === "paid" ? "default" : w.status === "rejected" ? "destructive" : "secondary"} className="text-[10px]">
                            {w.status === "pending" ? "Pendente" : w.status === "approved" ? "Aprovado" : w.status === "paid" ? "Pago" : "Rejeitado"}
                          </Badge>
                        </td>
                        <td className="p-4">
                          {w.status === "pending" && (
                            <div className="flex gap-1">
                              <Button size="sm" variant="ghost" onClick={() => updateWithdrawal(w.id, "approved")} className="text-green-600 h-7 text-xs">
                                Aprovar
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => updateWithdrawal(w.id, "rejected")} className="text-destructive h-7 text-xs">
                                Rejeitar
                              </Button>
                            </div>
                          )}
                          {w.status === "approved" && (
                            <Button size="sm" variant="ghost" onClick={() => updateWithdrawal(w.id, "paid")} className="text-green-600 h-7 text-xs">
                              Marcar Pago
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>

        {/* Materials Tab */}
        <TabsContent value="materials" className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-display font-semibold">Materiais de Divulgação</h3>
            <Button size="sm" onClick={() => setMaterialDialogOpen(true)} className="bg-gradient-primary text-primary-foreground">
              <Plus className="h-4 w-4 mr-2" /> Adicionar Material
            </Button>
          </div>

          {materials.length === 0 ? (
            <div className="rounded-xl border border-border bg-card p-12 text-center text-muted-foreground">
              <Image className="h-8 w-8 mx-auto mb-2 opacity-30" />
              <p className="text-sm">Nenhum material cadastrado</p>
            </div>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {materials.map(m => (
                <div key={m.id} className="rounded-xl border border-border bg-card overflow-hidden">
                  {m.type === "image" && m.file_url && (
                    <img src={m.file_url} alt={m.title} className="w-full h-32 object-cover" />
                  )}
                  <div className="p-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium">{m.title}</p>
                      <Badge variant="secondary" className="text-[9px]">{m.type}</Badge>
                    </div>
                    {m.description && <p className="text-xs text-muted-foreground">{m.description}</p>}
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => deleteMaterial(m.id)} className="text-destructive h-7 text-xs">
                        <Trash2 className="h-3 w-3 mr-1" /> Excluir
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Analytics Tab */}
        <TabsContent value="analytics" className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div className="rounded-xl border border-border bg-card p-4">
              <h3 className="text-sm font-display font-semibold mb-3">Cliques por Origem</h3>
              {sourceChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={sourceChartData}>
                    <XAxis dataKey="source" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} />
                    <Tooltip />
                    <Bar dataKey="cliques" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : <p className="text-xs text-muted-foreground text-center py-8">Sem dados</p>}
            </div>
            <div className="rounded-xl border border-border bg-card p-4">
              <h3 className="text-sm font-display font-semibold mb-3">Top Afiliados</h3>
              <div className="space-y-2">
                {affiliates
                  .filter(a => a.status === "active")
                  .sort((a, b) => b.total_earned - a.total_earned)
                  .slice(0, 5)
                  .map((a, i) => {
                    const affClicks = clicks.filter(c => c.affiliate_id === a.id).length;
                    const affRefs = referrals.filter(r => r.affiliate_id === a.id).length;
                    return (
                      <div key={a.id} className="flex items-center justify-between p-3 rounded-lg bg-secondary/50">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-bold text-muted-foreground">#{i + 1}</span>
                          <div>
                            <p className="text-sm font-medium">{a.name}</p>
                            <p className="text-[10px] text-muted-foreground">{affClicks} cliques · {affRefs} conversões</p>
                          </div>
                        </div>
                        <p className="text-sm font-bold">{fmt(a.total_earned)}</p>
                      </div>
                    );
                  })}
              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Create/Edit Affiliate Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">{editing ? "Editar Afiliado" : "Novo Afiliado"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label className="text-xs">Nome</Label>
                <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="h-9 text-sm" /></div>
              <div className="space-y-1.5"><Label className="text-xs">Email</Label>
                <Input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} className="h-9 text-sm" /></div>
            </div>
            <div className="space-y-1.5"><Label className="text-xs">Código de Indicação</Label>
              <Input value={form.code} onChange={e => setForm(f => ({ ...f, code: e.target.value.toUpperCase() }))} className="h-9 text-sm font-mono" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label className="text-xs">Tipo de Comissão</Label>
                <Select value={form.commission_type} onValueChange={v => setForm(f => ({ ...f, commission_type: v }))}>
                  <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="recurring">Recorrente</SelectItem>
                    <SelectItem value="one_time">Única</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label className="text-xs">Comissão (%)</Label>
                <Input type="number" min="0" max="100" value={form.commission_percentage} onChange={e => setForm(f => ({ ...f, commission_percentage: parseFloat(e.target.value) || 0 }))} className="h-9 text-sm" /></div>
            </div>
            <div className="space-y-1.5"><Label className="text-xs">Valor Fixo Adicional (R$)</Label>
              <Input type="number" min="0" step="0.01" value={form.commission_fixed} onChange={e => setForm(f => ({ ...f, commission_fixed: parseFloat(e.target.value) || 0 }))} className="h-9 text-sm" /></div>
            <div className="flex items-center gap-2">
              <Switch checked={form.is_active} onCheckedChange={v => setForm(f => ({ ...f, is_active: v }))} />
              <Label className="text-xs">Ativo</Label>
            </div>
            <Button onClick={save} disabled={saving} className="w-full bg-gradient-primary text-primary-foreground">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Check className="h-4 w-4 mr-2" />}
              {editing ? "Salvar" : "Criar Afiliado"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Material Dialog */}
      <Dialog open={materialDialogOpen} onOpenChange={setMaterialDialogOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">Adicionar Material</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs">Título</Label>
              <Input value={materialForm.title} onChange={e => setMaterialForm(f => ({ ...f, title: e.target.value }))} className="h-9 text-sm" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Descrição</Label>
              <Input value={materialForm.description} onChange={e => setMaterialForm(f => ({ ...f, description: e.target.value }))} className="h-9 text-sm" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Tipo</Label>
                <Select value={materialForm.type} onValueChange={v => setMaterialForm(f => ({ ...f, type: v }))}>
                  <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="image">Imagem</SelectItem>
                    <SelectItem value="video">Vídeo</SelectItem>
                    <SelectItem value="text">Texto/Copy</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Plataforma</Label>
                <Select value={materialForm.platform} onValueChange={v => setMaterialForm(f => ({ ...f, platform: v }))}>
                  <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas</SelectItem>
                    <SelectItem value="instagram">Instagram</SelectItem>
                    <SelectItem value="facebook">Facebook</SelectItem>
                    <SelectItem value="whatsapp">WhatsApp</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {(materialForm.type === "image" || materialForm.type === "video") && (
              <div className="space-y-1.5">
                <Label className="text-xs">Arquivo</Label>
                <Input type="file" accept={materialForm.type === "image" ? "image/*" : "video/*"}
                  onChange={e => setMaterialFile(e.target.files?.[0] || null)} className="h-9 text-sm" />
              </div>
            )}
            {materialForm.type === "text" && (
              <div className="space-y-1.5">
                <Label className="text-xs">Texto/Copy</Label>
                <Textarea value={materialForm.content} onChange={e => setMaterialForm(f => ({ ...f, content: e.target.value }))} rows={4} />
              </div>
            )}
            <Button onClick={saveMaterial} disabled={savingMaterial || !materialForm.title} className="w-full bg-gradient-primary text-primary-foreground">
              {savingMaterial ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
              Salvar Material
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminAffiliatesTab;
